import { Alert, Button, LibCombobox, lucideIcons } from '@round/creatorbase-ui';
import usePoolCombobox, { PoolItem } from '../usePoolCombobox';
import styles from './PoolSelect.module.css';

const { AlertTriangle, ChevronDownIcon } = lucideIcons;

type Props = {
    clientId: number | undefined;
    isEnabled: boolean;
    selectedPool: PoolItem | null;
    isLoading: boolean;
    selectedCreatorCount: number;
    onSelect: (pool: PoolItem) => void;
};

const PoolSelect = ({ clientId, isEnabled, selectedPool, isLoading, selectedCreatorCount, onSelect }: Props) => {
    const { comboboxProps, noItemsFound, onItemsListScroll } = usePoolCombobox({ clientId, isEnabled });

    const handleValueChange = (item: PoolItem | null) => {
        // Re-picking the current pool would clear the creator selection for nothing.
        if (item && item.value !== selectedPool?.value) {
            onSelect(item);
        }
    };

    return (
        <div className={styles.poolSelect}>
            <p className={styles.label}>Pool:</p>

            <LibCombobox
                {...comboboxProps}
                value={selectedPool}
                isItemEqualToValue={isSameItem}
                onValueChange={handleValueChange}
            >
                <LibCombobox.Trigger
                    render={
                        <Button variant="ghost" size="xs" aria-label="Pool" className={styles.trigger}>
                            {getTriggerLabel({ selectedPool, isLoading })}
                            <ChevronDownIcon className={styles.triggerChevron} />
                        </Button>
                    }
                />

                <LibCombobox.Content align="start" className={styles.content}>
                    <LibCombobox.Input placeholder="Search pools…" showTrigger={false} />

                    {selectedCreatorCount > 0 && (
                        <Alert className={styles.warning}>
                            <AlertTriangle className={styles.warningIcon} />
                            <Alert.Description className={styles.warningText}>
                                Changing pool clears the {selectedCreatorCount} creator
                                {selectedCreatorCount === 1 ? '' : 's'} you have selected
                            </Alert.Description>
                        </Alert>
                    )}

                    <LibCombobox.Empty>{noItemsFound}</LibCombobox.Empty>
                    <LibCombobox.List onScroll={onItemsListScroll}>
                        {(item: PoolItem) => (
                            <LibCombobox.Item key={item.value} value={item}>
                                {item.label}
                            </LibCombobox.Item>
                        )}
                    </LibCombobox.List>
                </LibCombobox.Content>
            </LibCombobox>
        </div>
    );
};

const isSameItem = (a: PoolItem | null, b: PoolItem | null) => a?.value === b?.value;

const getTriggerLabel = ({ selectedPool, isLoading }: Pick<Props, 'selectedPool' | 'isLoading'>) => {
    if (selectedPool) {
        return selectedPool.label;
    }

    return isLoading ? 'Loading pools…' : 'Select pool';
};

export default PoolSelect;
